const { getFieldType } = require('./helper');

/** Formik keys every field by its Strapi component row id. */
function fieldKey(field) {
  return `field_${field.id}`;
}

/**
 * The field takes part in display values and submission.
 *
 * A field marked `isHidden` in the CMS still counts here, so it reaches `getDisplayValues`
 * and the payload — but see `isFieldRendered`, because it is deliberately not validated.
 *
 * Note what this does *not* mean: `getInitialFormValues` skips `isHidden` fields outright,
 * before it would apply `preFilledValue`, so such a field has no entry in Formik state and
 * serialises as an undefined value. It is submitted, but empty. That is pre-existing and
 * predates this module — do not read "active" as "prefilled".
 *
 * Callers read `__typename` off the returned type object, so this requires a type to be
 * present but does not verify that the type is one `FORM_FIELDS` knows.
 *
 * Reads defensively: three of the five call sites this replaces indexed
 * `formDisplayState` without a guard and took the whole form down when an entry was
 * missing.
 */
function isFieldActive(field, formDisplayState) {
  return Boolean(
    formDisplayState?.[fieldKey(field)]?.isVisible
    && getFieldType(field),
  );
}

/**
 * The field is drawn on screen — and is therefore also validated, focusable, and worth an
 * entry in the error summary. Anything the user cannot see must not be able to block a
 * submit they have no way to fix.
 */
function isFieldRendered(field, formDisplayState) {
  return isFieldActive(field, formDisplayState) && !field.isHidden;
}

/**
 * The field occupies a slot in a group's grid.
 *
 * Deliberately does *not* require a type, unlike `isFieldActive`: a field whose type the
 * editor never picked still gets a slot, because `FormField` draws a "Kein Feldtyp
 * ausgewählt." alert in it. That alert is the only signal an editor gets that they left a
 * field half-configured, and a form that silently omits a question loses leads quietly.
 *
 * So a typeless field is drawn but — being neither active nor rendered — is left out of
 * form state, validation, submission and the error summary.
 */
function isFieldSlotted(field, formDisplayState) {
  return Boolean(
    formDisplayState?.[fieldKey(field)]?.isVisible
    && !field.isHidden,
  );
}

function getSlottedFields(fields, formDisplayState) {
  return (fields ?? []).filter((field) => isFieldSlotted(field, formDisplayState));
}

function getRenderedFields(fields, formDisplayState) {
  return (fields ?? []).filter((field) => isFieldRendered(field, formDisplayState));
}

function getRenderedFieldKeys(fields, formDisplayState) {
  return getRenderedFields(fields, formDisplayState).map(fieldKey);
}

module.exports = {
  fieldKey,
  isFieldActive,
  isFieldRendered,
  isFieldSlotted,
  getSlottedFields,
  getRenderedFields,
  getRenderedFieldKeys,
};
